import type { ComicBubbleObject, Point } from "./internalTypes";
import type { Rect } from "./comicTypes";

export interface BubbleTailCircle extends Point {
  radius: number;
}

function boundsFromPoints(points: ReadonlyArray<Point>): Rect {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minimumX = Math.min(...xs);
  const maximumX = Math.max(...xs);
  const minimumY = Math.min(...ys);
  const maximumY = Math.max(...ys);
  return {
    x: minimumX,
    y: minimumY,
    width: maximumX - minimumX,
    height: maximumY - minimumY,
  };
}

function unionBounds(first: Rect, second: Rect): Rect {
  const minimumX = Math.min(first.x, second.x);
  const minimumY = Math.min(first.y, second.y);
  const maximumX = Math.max(first.x + first.width, second.x + second.width);
  const maximumY = Math.max(first.y + first.height, second.y + second.height);
  return {
    x: minimumX,
    y: minimumY,
    width: maximumX - minimumX,
    height: maximumY - minimumY,
  };
}

export function getThoughtBubbleBodyPoints(
  object: Pick<ComicBubbleObject, "width" | "height">,
): Point[] {
  const points: Point[] = [];
  const lobes = 14;
  for (let index = 0; index < lobes; index += 1) {
    const angle = (index / lobes) * Math.PI * 2;
    const radiusX = object.width * (0.44 + (index % 2) * 0.04);
    const radiusY = object.height * (0.42 + ((index + 1) % 2) * 0.05);
    points.push({ x: Math.cos(angle) * radiusX, y: Math.sin(angle) * radiusY });
  }
  return points;
}

export function getShoutBubbleBodyPoints(
  object: Pick<ComicBubbleObject, "width" | "height">,
): Point[] {
  const points: Point[] = [];
  const spikes = 18;
  for (let index = 0; index < spikes * 2; index += 1) {
    const angle = (index / (spikes * 2)) * Math.PI * 2;
    const radius = index % 2 === 0 ? 1 : 0.78;
    points.push({
      x: Math.cos(angle) * object.width * 0.5 * radius,
      y: Math.sin(angle) * object.height * 0.5 * radius,
    });
  }
  return points;
}

export function getSpeechBubbleTailPoints(
  object: Pick<ComicBubbleObject, "width" | "height" | "data">,
): Point[] {
  const direction = object.data.tailDirection ?? "bottom-right";
  if (direction === "left" || direction === "right") {
    const side = direction === "left" ? -1 : 1;
    return [
      { x: side * object.width * 0.45, y: object.height * 0.08 },
      { x: side * object.width * 0.67, y: object.height * 0.24 },
      { x: side * object.width * 0.42, y: object.height * 0.3 },
    ];
  }
  const side = direction === "bottom-left" ? -1 : 1;
  return [
    { x: side * object.width * 0.18, y: object.height * 0.42 },
    { x: side * object.width * 0.38, y: object.height * 0.68 },
    { x: side * object.width * 0.04, y: object.height * 0.47 },
  ];
}

export function getThoughtBubbleTailCircles(
  object: Pick<ComicBubbleObject, "width" | "height" | "data">,
): BubbleTailCircle[] {
  const direction = object.data.tailDirection ?? "bottom-right";
  const side = direction.includes("left") ? -1 : 1;
  return [
    {
      x: side * object.width * 0.26,
      y: object.height * 0.58,
      radius: object.height * 0.08,
    },
    {
      x: side * object.width * 0.34,
      y: object.height * 0.72,
      radius: object.height * 0.05,
    },
  ];
}

/**
 * Returns renderer-aligned bounds in coordinates local to the bubble centre.
 * The body, deterministic tail geometry, and centred Canvas stroke are all
 * included so clamping and persisted-state validation share one model.
 */
export function getBubbleLocalVisualBounds(object: ComicBubbleObject): Rect {
  const width = Number.isFinite(object.width) ? Math.max(0, object.width) : 0;
  const height = Number.isFinite(object.height) ? Math.max(0, object.height) : 0;
  const safeObject = { ...object, width, height };
  let bounds: Rect;

  if (object.type === "thoughtBubble") {
    bounds = boundsFromPoints(getThoughtBubbleBodyPoints(safeObject));
    for (const circle of getThoughtBubbleTailCircles(safeObject)) {
      bounds = unionBounds(bounds, {
        x: circle.x - circle.radius,
        y: circle.y - circle.radius,
        width: circle.radius * 2,
        height: circle.radius * 2,
      });
    }
  } else if (object.type === "shoutBubble") {
    bounds = boundsFromPoints(getShoutBubbleBodyPoints(safeObject));
  } else {
    bounds = { x: -width / 2, y: -height / 2, width, height };
    if (object.type === "speechBubble") {
      bounds = unionBounds(bounds, boundsFromPoints(getSpeechBubbleTailPoints(safeObject)));
    }
  }

  const halfStroke =
    Number.isFinite(object.data.strokeWidth) && object.data.strokeWidth > 0
      ? object.data.strokeWidth / 2
      : 0;
  return {
    x: bounds.x - halfStroke,
    y: bounds.y - halfStroke,
    width: bounds.width + halfStroke * 2,
    height: bounds.height + halfStroke * 2,
  };
}

export function getBubbleRotatedVisualBounds(object: ComicBubbleObject): Rect {
  const local = getBubbleLocalVisualBounds(object);
  const centre = { x: object.x + object.width / 2, y: object.y + object.height / 2 };
  const radians = (object.rotation * Math.PI) / 180;
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  const corners = [
    { x: local.x, y: local.y },
    { x: local.x + local.width, y: local.y },
    { x: local.x + local.width, y: local.y + local.height },
    { x: local.x, y: local.y + local.height },
  ].map((point) => ({
    x: centre.x + point.x * cosine - point.y * sine,
    y: centre.y + point.x * sine + point.y * cosine,
  }));
  return boundsFromPoints(corners);
}
